/* VastraGharr Luxe 3.0 — product card behaviour
   Markup: snippets/card-product.liquid, snippets/vg-card-swatches.liquid (styles in assets/vg-card.css)

   - Touch mini-gallery: on touch phones/tablets the card image becomes a swipeable strip of the first 3–4
     product photos with tiny dots. Photos 2+ are only requested once the card is close to the viewport
     (photo 2 straight away, the rest on the first swipe). Each slide is a real link, so a tap still opens
     the product. Native scroll-snap with overscroll containment, listeners on the card's own track only.
     Cards that sit inside a horizontally scrolling row (product sliders) keep a single photo, so swiping
     there always moves the row and never gets captured by a card.
   - Colour swatches: hover (desktop) previews, tap / click selects the colour: the card photo switches to
     that colour's variant photo and every card link (and the quick add modal) points at that variant.
   - Quick add success: the card "+" button shows a check ("Added") for 1.6s after the product reaches the bag.
   - Keeps exactly one [data-vg-vt-source] / [data-vg-card-visible] per card on the photo the shopper sees,
     for the view transition into the product page and the fly-to-bag animation.
   Safe to load more than once (guarded), works for cards added later (facets, load more, fragments,
   theme editor) through one MutationObserver. */
(() => {
  'use strict';

  if (window.VG && window.VG.cardsReady) return;
  const VG = (window.VG = window.VG || {});
  VG.cardsReady = true;

  const touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const WIDTHS = [360, 540, 720, 900, 1080];
  const ADDED_MS = 1600;
  const MARK_VISIBLE = 'data-vg-card-visible';
  const MARK_VT = 'data-vg-vt-source';

  const closest = (node, selector) => (node && node.closest ? node.closest(selector) : null);

  function collect(root, selector) {
    if (!root || root.nodeType !== 1) return [];
    const list = Array.from(root.querySelectorAll(selector));
    if (root.matches(selector)) list.unshift(root);
    return list;
  }

  /* ---------- Shopify CDN image URLs ---------- */
  function withWidth(url, width) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.indexOf('?') === -1 ? '?' : '&'}width=${width}`;
  }

  const srcsetFor = (url) => WIDTHS.map((width) => `${withWidth(url, width)} ${width}w`).join(', ');

  /* ---------- Visible photo markers (view transition + fly-to-bag) ---------- */
  function setVisible(card, img) {
    if (!card || !img) return;
    card.querySelectorAll(`[${MARK_VISIBLE}]`).forEach((el) => {
      if (el === img) return;
      el.removeAttribute(MARK_VISIBLE);
      el.removeAttribute(MARK_VT);
    });
    img.setAttribute(MARK_VISIBLE, '');
    img.setAttribute(MARK_VT, '');
  }

  /* ---------- Mini-gallery ---------- */
  const galleries = new Set(); // media elements with a built gallery
  const deferred = new Set(); // media elements skipped because their card sits in a scrolling row

  function isInHorizontalScroller(element) {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      if (node.hasAttribute('data-vg-card-track')) continue;
      const overflow = window.getComputedStyle(node).overflowX;
      if ((overflow === 'auto' || overflow === 'scroll') && node.scrollWidth > node.clientWidth + 1) return true;
    }
    return false;
  }

  function loadSlide(img) {
    if (!img || !img.dataset.vgSrcset) return;
    img.srcset = img.dataset.vgSrcset;
    img.src = img.dataset.vgSrc;
    delete img.dataset.vgSrcset;
    delete img.dataset.vgSrc;
  }

  function markLoaded(slide, img) {
    if (img.complete && img.naturalWidth > 0) {
      slide.classList.add('is-loaded');
      return;
    }
    img.addEventListener('load', () => slide.classList.add('is-loaded'), { once: true });
  }

  function currentIndex(track) {
    const width = track.clientWidth || 1;
    return Math.max(0, Math.round(Math.abs(track.scrollLeft) / width));
  }

  function updateGallery(media) {
    const track = media.querySelector('[data-vg-card-track]');
    if (!track) return;
    const slides = Array.from(track.children);
    const index = Math.min(slides.length - 1, currentIndex(track));
    media.querySelectorAll('.vg-card__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    const img = slides[index] ? slides[index].querySelector('img') : null;
    if (img) setVisible(media.closest('.vg-card'), img);
  }

  function resetGallery(media) {
    const track = media.querySelector('[data-vg-card-track]');
    if (!track || track.scrollLeft === 0) return;
    track.scrollTo({ left: 0, behavior: reduceMotion.matches || media.classList.contains('is-gallery-locked') ? 'auto' : 'smooth' });
  }

  function setLocked(media, locked) {
    media.classList.toggle('is-gallery-locked', locked);
    if (locked) {
      const track = media.querySelector('[data-vg-card-track]');
      if (track) track.scrollLeft = 0;
      updateGallery(media);
    }
  }

  function buildGallery(media) {
    if (media.dataset.vgGalleryState === 'built') return;
    const card = media.closest('.vg-card');
    const primary = media.querySelector('.vg-card__img--primary');
    let urls = [];
    try {
      urls = JSON.parse(media.getAttribute('data-vg-gallery') || '[]');
    } catch (error) {
      urls = [];
    }
    urls = Array.isArray(urls) ? urls.filter((url) => typeof url === 'string' && url) : [];
    if (!card || !primary || !urls.length) {
      media.dataset.vgGalleryState = 'none';
      return;
    }

    if (isInHorizontalScroller(media)) {
      media.dataset.vgGalleryState = 'deferred';
      deferred.add(media);
      return;
    }
    deferred.delete(media);

    const link = card.querySelector('.vg-card__link');
    const href = link ? link.getAttribute('href') : '';
    const sizes = primary.getAttribute('sizes') || '50vw';
    const track = document.createElement('div');
    track.className = 'vg-card__track';
    track.setAttribute('data-vg-card-track', '');

    const makeSlide = () => {
      const slide = document.createElement('a');
      slide.className = 'vg-card__slide vg-img';
      if (href) slide.setAttribute('href', href);
      slide.setAttribute('tabindex', '-1');
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('draggable', 'false');
      track.appendChild(slide);
      return slide;
    };

    const first = makeSlide();
    first.appendChild(primary);
    markLoaded(first, primary);

    urls.forEach((url, index) => {
      const slide = makeSlide();
      const img = document.createElement('img');
      img.className = 'vg-card__img vg-card__img--slide';
      img.alt = '';
      img.decoding = 'async';
      img.setAttribute('draggable', 'false');
      img.setAttribute('sizes', sizes);
      img.setAttribute('fetchpriority', 'low');
      img.dataset.vgSrcset = srcsetFor(url);
      img.dataset.vgSrc = withWidth(url, 540);
      slide.appendChild(img);
      markLoaded(slide, img);
      // The next photo is fetched now so the first swipe never lands on an empty frame.
      if (index === 0) loadSlide(img);
    });

    const dots = document.createElement('span');
    dots.className = 'vg-card__dots';
    dots.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < urls.length + 1; i += 1) {
      const dot = document.createElement('span');
      dot.className = `vg-card__dot${i === 0 ? ' is-active' : ''}`;
      dots.appendChild(dot);
    }

    media.insertBefore(track, media.firstChild);
    media.appendChild(dots);
    media.classList.add('is-gallery');
    media.dataset.vgGalleryState = 'built';
    galleries.add(media);
    setVisible(card, primary);

    let frame = null;
    let restLoaded = false;
    const loadRest = () => {
      if (restLoaded) return;
      restLoaded = true;
      track.querySelectorAll('img').forEach(loadSlide);
    };
    track.addEventListener('touchstart', loadRest, { passive: true, once: true });
    track.addEventListener(
      'scroll',
      () => {
        loadRest();
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          updateGallery(media);
        });
      },
      { passive: true }
    );
  }

  const nearObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              nearObserver.unobserve(entry.target);
              buildGallery(entry.target);
            });
          },
          { rootMargin: '300px 0px 500px 0px' }
        )
      : null;

  function watchGalleries(root) {
    if (!touchQuery.matches || !nearObserver) return;
    collect(root, '[data-vg-gallery]').forEach((media) => {
      if (media.dataset.vgGalleryState) return;
      media.dataset.vgGalleryState = 'watch';
      nearObserver.observe(media);
    });
  }

  function recheckGalleries() {
    deferred.forEach((media) => {
      if (!media.isConnected) {
        deferred.delete(media);
        return;
      }
      if (!isInHorizontalScroller(media)) {
        media.dataset.vgGalleryState = '';
        buildGallery(media);
      }
    });
    galleries.forEach((media) => {
      if (!media.isConnected) {
        galleries.delete(media);
        return;
      }
      setLocked(media, isInHorizontalScroller(media));
    });
  }

  let resizeTimer = null;
  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(recheckGalleries, 300);
    },
    { passive: true }
  );

  const onTouchChange = () => {
    if (touchQuery.matches) watchGalleries(document.body);
  };
  if (typeof touchQuery.addEventListener === 'function') touchQuery.addEventListener('change', onTouchChange);

  /* ---------- Colour swatches ---------- */
  function rememberOriginal(card) {
    if (card.dataset.vgOrigHref !== undefined) return;
    const link = card.querySelector('.vg-card__link');
    card.dataset.vgOrigHref = link ? link.getAttribute('href') || '' : '';
    const img = card.querySelector('.vg-card__img--primary');
    if (img) {
      img.dataset.vgOrigSrc = img.getAttribute('src') || '';
      img.dataset.vgOrigSrcset = img.getAttribute('srcset') || '';
    }
  }

  function showPhoto(card, url) {
    const img = card.querySelector('.vg-card__img--primary');
    if (!img) return;
    rememberOriginal(card);
    if (url) {
      const srcset = srcsetFor(url);
      if (img.getAttribute('srcset') !== srcset) {
        img.setAttribute('srcset', srcset);
        img.setAttribute('src', withWidth(url, 720));
      }
    } else if (img.dataset.vgOrigSrc) {
      if (img.getAttribute('src') !== img.dataset.vgOrigSrc) {
        if (img.dataset.vgOrigSrcset) img.setAttribute('srcset', img.dataset.vgOrigSrcset);
        else img.removeAttribute('srcset');
        img.setAttribute('src', img.dataset.vgOrigSrc);
      }
    }
    const media = img.closest('.vg-card__media');
    if (media && media.classList.contains('is-gallery')) resetGallery(media);
    setVisible(card, img);
  }

  function setLinks(card, url) {
    rememberOriginal(card);
    const href = url || card.dataset.vgOrigHref;
    if (!href) return;
    card.querySelectorAll('.vg-card__link, .vg-card__slide').forEach((link) => link.setAttribute('href', href));
    card.querySelectorAll('modal-opener [data-product-url]').forEach((button) => button.setAttribute('data-product-url', href));
  }

  function selectedSwatch(card) {
    return card.querySelector('[data-vg-swatch][aria-pressed="true"]');
  }

  function selectSwatch(card, swatch) {
    card.querySelectorAll('[data-vg-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === swatch));
    });
    card.classList.toggle('vg-card--swatched', !!swatch);
    card.classList.remove('is-swatch-preview');
    showPhoto(card, swatch ? swatch.getAttribute('data-vg-swatch-image') : '');
    setLinks(card, swatch ? swatch.getAttribute('data-vg-swatch-url') : '');
  }

  function previewSwatch(card, swatch) {
    const url = swatch.getAttribute('data-vg-swatch-image');
    if (!url) return;
    card.classList.add('is-swatch-preview');
    showPhoto(card, url);
  }

  function endPreview(card) {
    if (!card.classList.contains('is-swatch-preview')) return;
    card.classList.remove('is-swatch-preview');
    const selected = selectedSwatch(card);
    showPhoto(card, selected ? selected.getAttribute('data-vg-swatch-image') : '');
  }

  document.addEventListener('click', (event) => {
    const swatch = closest(event.target, '[data-vg-swatch]');
    if (!swatch) return;
    const card = swatch.closest('.vg-card');
    if (!card) return;
    event.preventDefault();
    const isSelected = swatch.getAttribute('aria-pressed') === 'true';
    selectSwatch(card, isSelected ? null : swatch);
  });

  document.addEventListener('mouseover', (event) => {
    if (!hoverQuery.matches) return;
    const swatch = closest(event.target, '[data-vg-swatch]');
    if (!swatch) return;
    const card = swatch.closest('.vg-card');
    if (card) previewSwatch(card, swatch);
  });

  document.addEventListener('mouseout', (event) => {
    const row = closest(event.target, '[data-vg-swatches]');
    if (!row || (event.relatedTarget && row.contains(event.relatedTarget))) return;
    const card = row.closest('.vg-card');
    if (card) endPreview(card);
  });

  // Tabbing away from the dots also ends a hover preview that is still showing.
  document.addEventListener('focusout', (event) => {
    const row = closest(event.target, '[data-vg-swatches]');
    if (!row || (event.relatedTarget && row.contains(event.relatedTarget))) return;
    const card = row.closest('.vg-card');
    if (card) endPreview(card);
  });

  /* ---------- Quick add: "Added" state ---------- */
  const addedTimers = new WeakMap();
  let lastTrigger = null;

  document.addEventListener(
    'click',
    (event) => {
      const button = closest(event.target, '.vg-card .vg-qa');
      if (!button) return;
      const card = button.closest('.vg-card');
      lastTrigger = { button, productId: card ? card.getAttribute('data-product-id') || '' : '', time: Date.now() };
    },
    true
  );

  function markAdded(button) {
    if (!button || !button.isConnected) return;
    window.clearTimeout(addedTimers.get(button));
    button.classList.remove('is-added');
    // Restart the check animation when the same button succeeds twice in a row.
    void button.offsetWidth;
    button.classList.add('is-added');
    addedTimers.set(
      button,
      window.setTimeout(() => button.classList.remove('is-added'), ADDED_MS)
    );
  }

  function onCartUpdate(event) {
    if (!event || event.source !== 'product-form' || !lastTrigger) return;
    const trigger = lastTrigger;
    if (Date.now() - trigger.time > 120000) {
      lastTrigger = null;
      return;
    }
    const data = event.cartData || {};
    const item = data.items && data.items.length ? data.items[0] : data;
    const productId = item && item.product_id ? String(item.product_id) : '';
    if (productId && trigger.productId && productId !== trigger.productId) return;
    lastTrigger = null;
    markAdded(trigger.button);
  }

  let cartBound = false;
  function bindCart() {
    if (cartBound) return;
    if (typeof window.subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    cartBound = true;
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  /* ---------- Discovery ---------- */
  function initCards(root) {
    watchGalleries(root);
  }

  let pending = [];
  let flushTimer = null;
  function flush() {
    flushTimer = null;
    const nodes = pending;
    pending = [];
    nodes.forEach((node) => {
      if (node.isConnected) initCards(node);
    });
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.hasAttribute('data-vg-gallery') || node.querySelector('[data-vg-gallery]'))) {
            pending.push(node);
          }
        });
      });
      if (pending.length && flushTimer === null) flushTimer = window.setTimeout(flush, 60);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:unload', (event) => {
    if (!nearObserver) return;
    collect(event.target, '[data-vg-gallery]').forEach((media) => {
      nearObserver.unobserve(media);
      galleries.delete(media);
      deferred.delete(media);
    });
  });

  function start() {
    bindCart();
    initCards(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  // pubsub.js may load after this file on some templates.
  window.addEventListener('load', bindCart, { once: true });
})();
